/**
 * Owners join their own circles on this device without PINs (`devices.bindAsOwner`, architecture §4):
 * the builder key vouches for the member flagged `isOwner`. Used by the wizard, View as, owner-mode
 * landing, the launcher's Settings and `/join`.
 */
import { useMemo, useState } from 'react';
import type { Id } from '@circles/convex';
import { setLastCircle } from '../launcher/storage';
import { storeBoundDevice } from './boundDevice';
import { api, convexClient } from './convex';
import { useCachedQuery } from './liveQuery';
import { useDeviceTokens } from './tokens';

/** Binds this device as the circle's owner member, stores the token and opens that circle next. Returns its storage key. */
export async function joinAsOwner(accountKey: string, circleId: Id<'circles'>, name?: string): Promise<string> {
  const res = await convexClient.mutation(api.devices.bindAsOwner, {
    accountKey,
    circleId,
    ...(name !== undefined ? { name } : {}),
    userAgent: navigator.userAgent,
  });
  const key = storeBoundDevice(res);
  setLastCircle(key);
  return key;
}

/** "A", "A and B", "A, B and C". */
export function listNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export type OwnerJoinOffer = {
  /** The account's circles with an owner member that this device holds no token for, oldest first. */
  circles: { id: Id<'circles'>; name: string }[];
  /** "Join Family and Donegal on this device". */
  label: string;
  busy: boolean;
  error: string | null;
  /** Binds each circle in turn; resolves true when all of them joined. */
  join: () => Promise<boolean>;
};

/** What this device could join with the builder key on it (nothing without one). */
export function useOwnerJoinOffer(accountKey: string | null): OwnerJoinOffer {
  const owned = useCachedQuery(api.circles.list, accountKey ? { accountKey } : 'skip').data;
  const tokens = useDeviceTokens();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const circles = useMemo(() => {
    if (!owned) return [];
    const joined = new Set(Object.values(tokens).map((e) => e.circleId));
    return owned
      .filter((c) => c.ownerMember !== undefined && !joined.has(c._id))
      .sort((a, b) => a.createdAt - b.createdAt)
      .map((c) => ({ id: c._id, name: c.name }));
  }, [owned, tokens]);

  async function join(): Promise<boolean> {
    if (!accountKey || busy) return false;
    setBusy(true);
    setError(null);
    const failed: string[] = [];
    let first: string | null = null;
    for (const c of circles) {
      try {
        const key = await joinAsOwner(accountKey, c.id);
        first ??= key;
      } catch {
        failed.push(c.name);
      }
    }
    if (first) setLastCircle(first);
    setBusy(false);
    if (failed.length > 0) setError(`Couldn't join ${listNames(failed)}. Check your connection and try again.`);
    return failed.length === 0;
  }

  return { circles, label: `Join ${listNames(circles.map((c) => c.name))} on this device`, busy, error, join };
}
